import {
  Directive,
  ElementRef,
  inject,
  input,
  model,
  output,
  ViewContainerRef,
  effect
} from '@angular/core';
import { KeyCode } from '../../domain/key-code.enum';
import { OverlayOriginDirective } from './overlay-origin.directive';
import { OverlayService } from './overlay.service';
import type {
  InputSignal,
  ModelSignal,
  OnDestroy,
  OutputEmitterRef,
  TemplateRef
} from '@angular/core';

import type { OverlayRef } from '@angular/cdk/overlay';
import type { OverlayAsyncOpenEvent } from './overlay-factory.service';

export type OverlaySize = 'x-small' | 'small' | 'medium' | 'large' | 'content';

@Directive({
  selector: '[tdWbOverlayToggle]',
  exportAs: 'tdWbOverlayToggle',
  host: {
    '(click)': 'onClick($event)',
    '(keydown)': 'onKeyDown($event, $event.key)'
  }
})
export class OverlayToggleDirective extends OverlayOriginDirective implements OnDestroy {
  private static readonly SIZE_TO_PIXELS: Map<OverlaySize, number> = new Map<OverlaySize, number>([
    ['x-small', 250],
    ['small', 350],
    ['medium', 450],
    ['large', 750],
    ['content', 0]
  ]);

  /** Specify the overlay position by passing <PositionX>-<PositionY> string, OR <PositionY>-<OverlayOffset> string or Array<string>*/
  override readonly overlayPosition: InputSignal<string | Array<string> | undefined> = input<string | Array<string> | undefined>(undefined, { alias: 'tdWbOverlayPosition' });

  /** Specify the templateVariable to be used to populate the overlay content */
  readonly overlayTemplate: InputSignal<TemplateRef<unknown> | undefined> = input<TemplateRef<unknown> | undefined>(undefined, { alias: 'tdWbOverlayToggle' });

  /** Pass down arbitrary data. Normally not needed since ng-template receives the component context */
  readonly overlayData: InputSignal<unknown | undefined> = input<unknown | undefined>(undefined, { alias: 'tdWbOverlayData' });

  /** Defaults to sizing based on content, but could specify one of OverlaySizeType*/
  readonly overlaySize: InputSignal<OverlaySize> = input<OverlaySize>('content', { alias: 'tdWbOverlaySize' });

  /** Makes the overlay return OverlayAsyncOpenEvent onOpen and not open itself up until event.open gets called. */
  readonly asyncOpen: InputSignal<boolean> = input<boolean>(false, { alias: 'tdWbOverlayAsync' });

  readonly preventClickDefault: InputSignal<boolean> = input<boolean>(true, { alias: 'tdWbOverlayPreventClickDefault' });

  readonly open: ModelSignal<boolean> = model<boolean>(false, { alias: 'tdWbOverlayOpen' });

  readonly onAsyncOpen: OutputEmitterRef<OverlayAsyncOpenEvent> = output<OverlayAsyncOpenEvent>({ alias: 'tdWbOverlayAsyncOpen' });

  private overlayRef: OverlayRef;

  override readonly elementRef = inject(ElementRef);
  readonly viewContainerRef = inject(ViewContainerRef);
  private readonly overlayService = inject(OverlayService);

  private readonly role: string | null = this.elementRef.nativeElement.getAttribute('role');

  constructor() {
    super();
    // effect: calls OverlayToggleBase.toggle() in response to the open signal —
    // toggle() is an imperative CDK Overlay API; computed() is insufficient
    // because toggle() returns void and has additional side effects (event emission).
    effect((): void => {
      this.toggle(this.open());
    });
  }

  onClick(event: MouseEvent | KeyboardEvent): void {
    if (this.preventClickDefault()) {
      event.preventDefault();
    }
    if (this.asyncOpen()) {
      this.onAsyncOpen.emit({
        open: (): void => this.toggle(true)
      });
    } else {
      this.toggle();
    }
  }

  onKeyDown(event: KeyboardEvent, keyCode: string): void {
    if ((this.role === 'button' && (keyCode === KeyCode.ENTER || keyCode === KeyCode.SPACE)) ||
      (this.role === 'link' && keyCode === KeyCode.ENTER)) {
      this.onClick(event);
    }
  }

  ngOnDestroy(): void {
    this.toggle(false);
  }

  updatePosition(): void {
    if (this.overlayRef !== undefined) {
      this.overlayRef.updatePosition();
    }
  }

  private toggle(open: boolean = !this.isOpen()): void {
    if (open && !this.isOpen()) {
      this.overlayRef = this.overlayService.open({
        elementRef: this.elementRef,
        overlayTemplate: this.overlayTemplate(),
        viewContainerRef: this.viewContainerRef,
        positions: this.getPositions(),
        overlayData: this.overlayData(),
        overlaySize: OverlayToggleDirective.SIZE_TO_PIXELS.get(this.overlaySize())
      });
      this.open.set(true);
      this.overlayRef.detachments().subscribe((): void => {
        this.open.set(false);
        this.overlayRef = undefined;
      });
    }
    if (!open && this.isOpen()) {
      this.overlayService.close();
    }
  }

  private isOpen(): boolean {
    return this.overlayRef !== undefined;
  }
}
